import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
/**
 * EmailVerifiedPage — handles the `mode=verifyEmail&oobCode=...` deep link.
 *
 * Minimal baseline:
 *   1. Apply the action code (verifies the email at Firebase).
 *   2. Resolve the user's role from the most trustworthy source available:
 *        a. Firestore `users/{uid}.userType` (authoritative), if auth.currentUser exists
 *        b. localStorage `userType` (fallback for same-device flows)
 *      This is necessary because the verification link is often opened in a
 *      different browser/device than the one used for signup, where
 *      localStorage is empty.
 *   3. STUDENT: explicitly sign the student out via AuthContext.logout(),
 *      then hard-redirect to /student-login.
 *   4. TEACHER: hard-redirect to /teacher-dashboard, unchanged.
 */
export const EmailVerifiedPage = () => {
  const [searchParams] = useSearchParams();
  const {
    logout
  } = useAuth();
  const hasRunRef = useRef(false);
  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    const handleEmailVerification = async () => {
      const actionCode = searchParams.get('oobCode');
      const mode = searchParams.get('mode');
      if (mode === 'verifyEmail' && actionCode) {
        try {
          await applyActionCode(auth, actionCode);
          if (auth.currentUser) {
            try {
              await auth.currentUser.reload();
            } catch {
              // Non-fatal — the redirect target will still work
            }
          }
        } catch (error: any) {
          // auth/invalid-action-code typically means the link was already used.
          // Either way, fall through to the redirect so the user can sign in.
          if (error?.code !== 'auth/invalid-action-code') {
            console.error('[EmailVerifiedPage] applyActionCode error:', error);
          }
        }
      }
      // Resolve userType: prefer Firestore (authoritative), fall back to localStorage.
      // This handles the common case where the verification link is opened in a
      // different browser/device than signup, where localStorage is empty.
      let resolvedUserType: string | null = null;
      if (auth.currentUser?.uid) {
        try {
          const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            if (typeof data?.userType === 'string') {
              resolvedUserType = data.userType;
            }
          }
        } catch (err) {
          console.warn('[EmailVerifiedPage] Failed to read userType from Firestore (non-fatal):', err);
        }
      }
      if (!resolvedUserType) {
        resolvedUserType = localStorage.getItem('userType');
      }
      const isStudent = resolvedUserType === 'student';
      // Route to teacher dashboard ONLY when we have positive evidence of a
      // teacher account. Any other case (student, or unresolved role — e.g.
      // verification link opened on a different device where auth.currentUser
      // is null and localStorage is empty) defaults to /student-login. This
      // prevents unresolved students from being bounced into the teacher
      // login page by AuthGuard.
      if (resolvedUserType === 'teacher') {
        window.location.replace('/teacher-dashboard');
        return;
      }
      // Student branch (and safe default for unresolved role).
      // Clean sign-out before the redirect so the student lands on
      // /student-login with no lingering authenticated session. This
      // resolves the first-login-after-verification race entirely.
      // logout() sets the explicit-logout flag inside AuthContext, so the
      // cross-tab sign-out detector will not misfire.
      try {
        await logout();
      } catch (err) {
        console.error('[EmailVerifiedPage] Pre-redirect logout failed (non-fatal):', err);
      }
      window.location.replace('/student-login');
    };
    handleEmailVerification();
  }, [searchParams]);
  return <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]" data-id="element-3974">
      <div className="flex flex-col items-center" data-id="element-3975">
        <div className="w-12 h-12 border-4 border-[#e63946] border-t-transparent rounded-full animate-spin" data-id="element-3976"></div>
        <p className="mt-4 text-white text-sm" data-id="element-3977">Verifying your email...</p>
      </div>
    </div>;
};